import type {
  FeedbackCategory,
  FeedbackImpact,
  FeedbackStatus,
  ProductFeedbackType,
  UserRole,
} from '@doneverse/shared'

/**
 * URL search params for the admin Feedback Queue. Every filter lives here
 * rather than in component state, like `/admin/users` and `/tasks`: a triaged
 * view is then linkable ("the unassigned bugs" pasted into Slack), survives
 * browser back out of an item, and outlives the page unmounting.
 *
 * Absent means "no filter", so an untouched queue has a clean URL — and the
 * default view stays unfiltered on purpose (the API sorts new-and-unassigned
 * first, so untriaged work is already on top).
 */
export type AdminFeedbackSearch = {
  q?: string
  page?: number
  status?: FeedbackStatus
  category?: FeedbackCategory
  type?: ProductFeedbackType
  /** Submitter's role, not the owner's. */
  role?: UserRole
  /** A user id, or `FEEDBACK_OWNER_UNASSIGNED`. */
  owner?: string
  impact?: FeedbackImpact
}

export const FEEDBACK_TYPE_OPTIONS: ProductFeedbackType[] = ['bug_report', 'feature_request', 'general']

export const FEEDBACK_STATUS_OPTIONS: FeedbackStatus[] = [
  'new',
  'acknowledged',
  'in_progress',
  'escalated',
  'closed',
]

export const FEEDBACK_CATEGORY_OPTIONS: FeedbackCategory[] = [
  'bug',
  'feature_request',
  'general_feedback',
  'user_error',
]

export const FEEDBACK_IMPACT_FILTER_OPTIONS: FeedbackImpact[] = [
  'high',
  'medium',
  'low',
  'game_changer',
  'would_really_help',
  'nice_to_have',
]

/**
 * Every role that can reach the feedback dialog, which is every role — the
 * button lives in TopBarActions. The API accepts the whole enum as a
 * submitter_role filter, so leaving one out here just hides those submissions.
 */
export const FEEDBACK_ROLE_OPTIONS: UserRole[] = [
  'founder',
  'doer',
  'doing_team',
  'admin',
  'intake_team',
  'escalation_officer',
  'dsl',
  'supervisor',
  'support_staff',
]

/** A hand-edited URL is untrusted input — an unknown value means "no filter". */
function pick<T extends string>(value: unknown, options: readonly T[]): T | undefined {
  return typeof value === 'string' && (options as readonly string[]).includes(value)
    ? (value as T)
    : undefined
}

/** Drops params that are back at their default so they leave the URL. */
export function cleanAdminFeedbackSearch(search: AdminFeedbackSearch): AdminFeedbackSearch {
  const result: AdminFeedbackSearch = {}
  if (search.q) result.q = search.q
  if (search.page && search.page > 1) result.page = search.page
  if (search.status) result.status = search.status
  if (search.category) result.category = search.category
  if (search.type) result.type = search.type
  if (search.role) result.role = search.role
  if (search.owner) result.owner = search.owner
  if (search.impact) result.impact = search.impact
  return result
}

/** Route `validateSearch` for /admin/feedback. */
export function validateAdminFeedbackSearch(search: Record<string, unknown>): AdminFeedbackSearch {
  return cleanAdminFeedbackSearch({
    q: typeof search.q === 'string' ? search.q : undefined,
    page: typeof search.page === 'number' ? search.page : undefined,
    status: pick(search.status, FEEDBACK_STATUS_OPTIONS),
    category: pick(search.category, FEEDBACK_CATEGORY_OPTIONS),
    type: pick(search.type, FEEDBACK_TYPE_OPTIONS),
    role: pick(search.role, FEEDBACK_ROLE_OPTIONS),
    owner: typeof search.owner === 'string' ? search.owner : undefined,
    impact: pick(search.impact, FEEDBACK_IMPACT_FILTER_OPTIONS),
  })
}

/** Every filter cleared — spread over the current search to reset the queue. */
export const EMPTY_ADMIN_FEEDBACK_SEARCH: Record<keyof AdminFeedbackSearch, undefined> = {
  q: undefined,
  page: undefined,
  status: undefined,
  category: undefined,
  type: undefined,
  role: undefined,
  owner: undefined,
  impact: undefined,
}

export function hasAdminFeedbackFilters(search: AdminFeedbackSearch): boolean {
  return Boolean(
    search.q ||
    search.status ||
    search.category ||
    search.type ||
    search.role ||
    search.owner ||
    search.impact,
  )
}
